import { useState, useEffect, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

function getToken() {
  const role = sessionStorage.getItem("activeRole");
  if (role) return localStorage.getItem("token_" + role);
  for (const r of ["itadmin","superadmin","hr","employee","pm","intern","admin"]) {
    const t = localStorage.getItem("token_" + r); if (t) return t;
  }
  return null;
}

async function apiCall(path) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }
  });
  return res.json();
}

// ── Color palettes ──
const PALETTES = {
  priority: {
    "Critical": "#ef4444",
    "High":     "#f97316",
    "Medium":   "#eab308",
    "Low":      "#22c55e",
  },
  status: {
    "open":        "#6366f1",
    "in_progress": "#f59e0b",
    "resolved":    "#10b981",
    "closed":      "#94a3b8",
    "pending":     "#f97316",
    "approved":    "#22c55e",
    "rejected":    "#ef4444",
  },
  assetType: [
    "#6366f1","#f59e0b","#10b981","#ef4444",
    "#3b82f6","#ec4899","#14b8a6","#a855f7","#f97316",
  ],
};

function getColor(palette, key, idx = 0) {
  if (typeof palette === "object" && !Array.isArray(palette)) {
    return palette[key] || PALETTES.assetType[idx % PALETTES.assetType.length];
  }
  return palette[idx % palette.length];
}

// ── SVG Pie Chart ──
function PieChart({ data, palette, label, visible }) {
  const [hovered, setHovered] = useState(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setAnimated(true), 100);
      return () => clearTimeout(t);
    }
  }, [visible]);

  if (!data || data.length === 0) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:220, color:"#94a3b8", fontSize:13 }}>
        No data available
      </div>
    );
  }

  const total = data.reduce((s, d) => s + Number(d.count), 0);
  const cx = 110, cy = 110, r = 88, innerR = 48;

  // Build slices
  let angle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const val = Number(d.count);
    const pct = val / total;
    const sweep = pct * 2 * Math.PI;
    const startAngle = angle;
    angle += sweep;
    const endAngle = angle;
    const isHov = hovered === i;

    const rr = isHov ? r + 7 : r;
    const x1 = cx + rr * Math.cos(startAngle);
    const y1 = cy + rr * Math.sin(startAngle);
    const x2 = cx + rr * Math.cos(endAngle);
    const y2 = cy + rr * Math.sin(endAngle);
    const xi1 = cx + innerR * Math.cos(startAngle);
    const yi1 = cy + innerR * Math.sin(startAngle);
    const xi2 = cx + innerR * Math.cos(endAngle);
    const yi2 = cy + innerR * Math.sin(endAngle);
    const largeArc = sweep > Math.PI ? 1 : 0;
    const path = [
      `M ${xi1} ${yi1}`,
      `L ${x1} ${y1}`,
      `A ${rr} ${rr} 0 ${largeArc} 1 ${x2} ${y2}`,
      `L ${xi2} ${yi2}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${xi1} ${yi1}`,
      "Z"
    ].join(" ");

    const midAngle = startAngle + sweep / 2;
    const color = getColor(palette, d.name || d.priority || d.status || d.asset_type, i);

    return { path, color, pct, val, name: d.name || d.priority || d.status || d.asset_type, midAngle };
  });

  const hovSlice = hovered !== null ? slices[hovered] : null;

  return (
    <div style={{ position:"relative" }}>
      <svg
        width="220" height="220"
        style={{ display:"block", margin:"0 auto", overflow:"visible",
          opacity: animated ? 1 : 0,
          transform: animated ? "scale(1)" : "scale(0.85)",
          transition: "opacity 0.6s ease, transform 0.6s cubic-bezier(.34,1.56,.64,1)"
        }}
      >
        {slices.map((s, i) => (
          <path
            key={i}
            d={s.path}
            fill={s.color}
            stroke="#fff"
            strokeWidth={2}
            style={{
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(.34,1.56,.64,1)",
              filter: hovered === i ? `drop-shadow(0 4px 12px ${s.color}88)` : "none",
            }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}

        {/* Center label */}
        {hovSlice ? (
          <>
            <text x={cx} y={cy - 10} textAnchor="middle" fontSize={22} fontWeight={800} fill="#1e293b">
              {hovSlice.val}
            </text>
            <text x={cx} y={cy + 8} textAnchor="middle" fontSize={10} fill="#64748b">
              {hovSlice.name}
            </text>
            <text x={cx} y={cy + 22} textAnchor="middle" fontSize={11} fontWeight={700} fill={hovSlice.color}>
              {(hovSlice.pct * 100).toFixed(1)}%
            </text>
          </>
        ) : (
          <>
            <text x={cx} y={cy - 8} textAnchor="middle" fontSize={26} fontWeight={800} fill="#1e293b">
              {total}
            </text>
            <text x={cx} y={cy + 12} textAnchor="middle" fontSize={11} fill="#94a3b8">
              {label}
            </text>
          </>
        )}
      </svg>

      {/* Legend */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:"6px 12px", justifyContent:"center", marginTop:14 }}>
        {slices.map((s, i) => (
          <div key={i}
            style={{
              display:"flex", alignItems:"center", gap:6, cursor:"pointer",
              opacity: hovered === null || hovered === i ? 1 : 0.4,
              transition: "opacity 0.2s",
              background: hovered === i ? `${s.color}15` : "transparent",
              borderRadius: 6, padding:"2px 8px 2px 4px"
            }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            <div style={{ width:10, height:10, borderRadius:"50%", background:s.color, flexShrink:0 }} />
            <span style={{ fontSize:11, color:"#475569", fontWeight:500 }}>
              {s.name} <span style={{ color:s.color, fontWeight:700 }}>({s.val})</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Card wrapper with slide-in animation ──
function ChartCard({ title, subtitle, icon, children, delay = 0, visible }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(t);
    }
  }, [visible, delay]);

  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      boxShadow: "0 4px 24px rgba(0,0,0,.07)",
      padding: "22px 20px 20px",
      border: "1px solid #f1f5f9",
      opacity: show ? 1 : 0,
      transform: show ? "translateY(0)" : "translateY(28px)",
      transition: "opacity 0.55s ease, transform 0.55s cubic-bezier(.34,1.2,.64,1)",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
        <div style={{
          width:36, height:36, borderRadius:10,
          background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:16
        }}>{icon}</div>
        <div>
          <div style={{ fontWeight:700, fontSize:14, color:"#0f172a" }}>{title}</div>
          <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

// ── Summary stat ──
function StatPill({ label, val, color }) {
  return (
    <div style={{ background:`${color}12`, border:`1px solid ${color}30`, borderRadius:10, padding:"10px 16px", textAlign:"center" }}>
      <div style={{ fontSize:22, fontWeight:800, color }}>{val}</div>
      <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>{label}</div>
    </div>
  );
}

export default function ITAAnalyticsPg() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    apiCall("/itadmin/analytics")
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const ticketPriority  = (data?.ticketPriority  || []).map(r => ({ ...r, name: r.priority }));
  const ticketStatus    = (data?.ticketStatus    || []).map(r => ({ ...r, name: r.status  }));
  const totalByType     = (data?.totalByType     || []).map(r => ({ ...r, name: r.asset_type }));
  const availableByType = (data?.availableByType || []).map(r => ({ ...r, name: r.asset_type }));

  const totalAssets     = totalByType.reduce((s, r) => s + Number(r.count), 0);
  const totalAvailable  = availableByType.reduce((s, r) => s + Number(r.count), 0);
  const totalTickets    = ticketPriority.reduce((s, r) => s + Number(r.count), 0);

  return (
    <div style={{ minHeight:"100vh", background:"#f8fafc" }}>
      {/* Header */}
      <div style={{
        background:"linear-gradient(135deg,#1e1b4b 0%,#312e81 50%,#4f46e5 100%)",
        padding:"28px 28px 28px",
        position:"relative", overflow:"hidden"
      }}>
        {/* Decorative circles */}
        <div style={{ position:"absolute", top:-40, right:-40, width:200, height:200, borderRadius:"50%", background:"rgba(255,255,255,.05)" }} />
        <div style={{ position:"absolute", bottom:-60, left:60, width:150, height:150, borderRadius:"50%", background:"rgba(255,255,255,.04)" }} />

        <div style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(-16px)",
          transition: "all 0.6s ease"
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:8 }}>
            <div style={{ fontSize:28 }}>📊</div>
            <div>
              <div style={{ fontSize:22, fontWeight:800, color:"#fff", letterSpacing:-0.5 }}>IT Analytics</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,.6)", marginTop:2 }}>Real-time overview of tickets & assets</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding:"24px 24px 32px", marginTop:0, position:"relative", zIndex:1 }}>
        {/* Summary pills */}
        <div style={{
          display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:24,
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.5s ease 0.2s"
        }}>
          <StatPill label="Total Tickets" val={totalTickets} color="#6366f1" />
          <StatPill label="Total Assets"  val={totalAssets}  color="#10b981" />
          <StatPill label="Available"     val={totalAvailable} color="#f59e0b" />
        </div>

        {loading ? (
          <div style={{ textAlign:"center", padding:60 }}>
            <div style={{
              width:48, height:48, borderRadius:"50%",
              border:"4px solid #e2e8f0", borderTopColor:"#6366f1",
              animation:"spin 0.8s linear infinite", margin:"0 auto 16px"
            }} />
            <div style={{ color:"#94a3b8", fontSize:14 }}>Loading analytics...</div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : error ? (
          <div style={{ textAlign:"center", padding:40, color:"#ef4444" }}>⚠️ {error}</div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18 }}>
            <ChartCard
              icon="🎯"
              title="Ticket Priority"
              subtitle="Based on user-raised tickets"
              delay={0} visible={mounted}
            >
              <PieChart
                data={ticketPriority}
                palette={PALETTES.priority}
                label="Tickets"
                visible={mounted}
              />
            </ChartCard>

            <ChartCard
              icon="📋"
              title="Ticket Status"
              subtitle="Current resolution status"
              delay={120} visible={mounted}
            >
              <PieChart
                data={ticketStatus}
                palette={PALETTES.status}
                label="Tickets"
                visible={mounted}
              />
            </ChartCard>

            <ChartCard
              icon="💻"
              title="Total Assets"
              subtitle="All assets by type"
              delay={240} visible={mounted}
            >
              <PieChart
                data={totalByType}
                palette={PALETTES.assetType}
                label="Assets"
                visible={mounted}
              />
            </ChartCard>

            <ChartCard
              icon="✅"
              title="Available Assets"
              subtitle="Ready to assign, by type"
              delay={360} visible={mounted}
            >
              <PieChart
                data={availableByType}
                palette={PALETTES.assetType}
                label="Available"
                visible={mounted}
              />
            </ChartCard>
          </div>
        )}
      </div>
    </div>
  );
}
